import type { ReactElement } from "react"
import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useParams, useSearchParams } from "react-router-dom"

import { Grid, useMediaQuery, useTheme } from "@mui/material"
import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import LZString from "lz-string"
import type { MRT_PaginationState } from "material-react-table"

import { SideBar } from "../../../../components/UI/SideBar"
import { ProjectDecisionsTable } from "../../../../components/tables/ProjectDecisions"
import type { ListPredictionsOutput } from "../../../../types/generated/api/ListPredictionsOutput"
import { DecisionsContext } from "../../context/Context"
import { useLabels } from "../../hooks/useLabels"
import { useProjectPredictions } from "../../hooks/useProjectPredictions"
import { DecisionAnalysis } from "../DecisionAnalysis/DecisionAnalysis"
import { MetaData } from "../MetaData/MetaData"
import { RequestBreakDown } from "../RequestBreakDown/RequestBreakDown"

import styles from "./DecisionsSidebar.module.scss"

type ParamsType = {
  id: string
}

/**
 * Main Decisions component that provides the decisions management interface.
 * Manages state for project predictions, labels, filters, and pagination.
 * Renders the decisions table and sidebar with metadata and analysis.
 *
 * @returns React element containing the decisions interface
 */
export function Decisions(): ReactElement {
  const { id: projectId } = useParams<ParamsType>()
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()
  const isSmall = useMediaQuery(`(max-width:${theme.breakpoints.values.lg}px)`)

  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")

  const { t } = useTranslation("decisions", {
    keyPrefix: "projectDecisions",
  })
  const { t: tDecisions } = useTranslation("decisions", {
    keyPrefix: "decisions",
  })

  const [selectedRow, setSelectedRow] = useState<ListPredictionsOutput | null>(null)

  // state to create a ComponentIsMounting effect in functional react
  const [isMounted, setIsMounted] = useState<boolean>(false)

  const [filterObject, setFilterObject] = useState<Record<PropertyKey, unknown>>({})
  const [reviewLabel, setReviewLabel] = useState<"REVIEWED" | "REVOKED" | undefined>(undefined)

  const [jointOutputFilter, setJointOutputFilter] = useState<string | undefined>(undefined)
  const [reasonFilter, setReasonFilter] = useState<string | undefined>(undefined)
  const [feedbackFilter, setFeedbackFilter] = useState<string | undefined>(undefined)
  const [labelsSearchValue, setLabelsSearchValue] = useState<string | undefined>()

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // fetch model features (model predictions table data)
  const {
    isLoading: isProjectPredictionsLoading,
    isFetching: isProjectPredictionsFetching,
    data: projectPredictions,
  } = useProjectPredictions({
    enabled: !["", null, undefined].includes(projectId) && !!startDate && !!endDate && isMounted,
  })

  // fetch labels for actual decisions
  const {
    data: labelsRes,
    isFetchingNextPage: isFetchingLabelsNextPage,
    fetchNextPage: fetchNextLabelsPage,
    hasNextPage: hasNextLabelsPage,
    isFetching: isFetchingLabels,
  } = useLabels({
    labelsSearchValue: labelsSearchValue,
    notProvidedLabelName: t("status.notProvided"),
    onError: () => {
      NotificationUtils.toast(t("notifications.failedFetchingLabels"), { severity: "error" })
    },
  })

  // Reset pagination when date range changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [startDate, endDate])

  // onFiltersChange: set filters to query-params (encoded)
  useEffect(() => {
    if (isMounted) {
      const params: { [key: string]: string } = {}

      for (const [key, value] of searchParams.entries()) {
        params[key] = value
      }

      const stringifiedParams = JSON.stringify(filterObject)
      const compressed_object = LZString.compressToEncodedURIComponent(stringifiedParams)

      if (stringifiedParams !== "{}") {
        params["decision_filters"] = compressed_object
      } else {
        delete params["decision_filters"]
      }

      if (reviewLabel) params["reviewed"] = reviewLabel
      else delete params["reviewed"]

      params["page"] = pagination.pageIndex.toString()
      params["pageSize"] = pagination.pageSize.toString()

      if (jointOutputFilter) params["joint_output"] = jointOutputFilter
      else delete params["joint_output"]

      if (reasonFilter) params["reason"] = reasonFilter
      else delete params["reason"]

      if (feedbackFilter) params["feedback"] = feedbackFilter
      else delete params["feedback"]

      setSearchParams(params)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterObject, reviewLabel, jointOutputFilter, reasonFilter, feedbackFilter, pagination])

  // onMount: set filters present in the url to filter based on query params
  useEffect(() => {
    const decisionFilters = searchParams.get("decision_filters") as string
    const reviewed = searchParams.get("reviewed") as "REVIEWED" | "REVOKED" | undefined
    const page = parseInt(searchParams.get("page") ?? "0") ?? 0
    const size = parseInt(searchParams.get("pageSize") ?? "10") ?? 10

    const jointOutput = searchParams.get("joint_output") as string

    const reason = searchParams.get("reason") as string

    const feedback = searchParams.get("feedback") as string

    decisionFilters && setFilterObject(JSON.parse(LZString.decompressFromEncodedURIComponent(decisionFilters)))
    reviewed && setReviewLabel(reviewed)
    setPagination({ pageIndex: page, pageSize: size })

    jointOutput && setJointOutputFilter(jointOutput)

    reason && setReasonFilter(reason)

    feedback && setFeedbackFilter(feedback)

    !isMounted && setIsMounted(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Grid item xs={12}>
      <DecisionsContext.Provider
        value={{
          data: projectPredictions?.data ?? undefined,
          isFetching: isProjectPredictionsFetching,
          isLoading: isProjectPredictionsLoading,
          pagination: pagination,
          setPagination: setPagination,
          filterObject: filterObject,
          setFilterObject: setFilterObject,
          reviewLabel: reviewLabel,
          setReviewLabel: setReviewLabel,
          jointOutputFilter: jointOutputFilter,
          setJointOutputFilter: setJointOutputFilter,
          reasonFilter: reasonFilter,
          setReasonFilter: setReasonFilter,
          feedbackFilter: feedbackFilter,
          setFeedbackFilter: setFeedbackFilter,
          fetchNextLabelsPage,
          isFetchingLabelsNextPage: isFetchingLabelsNextPage || isFetchingLabels,
          hasNextLabelsPage,
          labels: labelsRes?.pages,
          setLabelsSearchValue,
          selectedRow,
          setSelectedRow,
        }}
      >
        <ProjectDecisionsTable />
        {selectedRow && (
          <SideBar
            isOpen={true}
            onClose={() => {
              setSelectedRow(null)
            }}
            title={tDecisions("riskAssessmentDecision")}
            closeIcon={{
              icon: "close",
              position: "right",
            }}
            variant={!isSmall ? "floating" : "default"}
            paperClassName={styles.sidebarPaper}
            isLoading={isProjectPredictionsFetching || isProjectPredictionsLoading}
          >
            <div className={styles.dialogBody}>
              <MetaData selectedUuid={selectedRow.uuid} />

              <div className={styles.bottomRow}>
                <div className={styles.left}>
                  <RequestBreakDown />
                </div>
                <div className={styles.right}>
                  <DecisionAnalysis />
                </div>
              </div>
            </div>
          </SideBar>
        )}
      </DecisionsContext.Provider>
    </Grid>
  )
}
